import { Store } from 'redux';
import i18next, {i18n, Resource} from 'i18next';
import moment from 'moment';
import llsAPI from 'axios';
import { localeToLanguage } from '~src/interfaces/session.types';
import { StoreI } from '~src/rootReducer';

import { asyncForEach } from '~src/utils';
import { apiConfig } from '../../config/server/server.index';

import logger from './logger/LlsLogger';
import { getBrowserPreferredSystemLocale, getSystemLocale, isSystemLocaleAllowed } from './session';

const momentTranslations: { [key: string]: { [key: string]: string } } = {
  de: {
    future: 'in %s',
    past: '%s ago',
    s: 'wenigen Sekunden',
    ss: '%d Sekunden',
    m: '1 Minute',
    mm: '%d Minuten',
    h: '1 Stunde',
    hh: '%d Stunden',
    d: '1 Tag',
    dd: '%d Tage',
    M: '1 Monat',
    MM: '%d Monate',
    y: '1 Jahr',
    yy: '%d Jahre',
  },
  en: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d seconds',
    m: '1 minute',
    mm: '%d minutes',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years',
  },
};

class Translations {
  STORAGE_PREFIX_TRANSLATION = 'translation_';
  STORAGE_PREFIX_TRANSLATION_WRITE_TIME = 'translation_update_';
  lastLocale = '';
  store: null | Store = null;

  // Check status of translation keys, fetch if required
  async initTranslations(availableLanguages: string[]) {
    const start = async () => {
      await asyncForEach(availableLanguages, async (language: string) => {
        const hasChanges = await this.fetchIsTranslationCacheOutdated(language);
        if (hasChanges) {
          const fetchedTranslations = await this.fetchTranslations(language);
          if (fetchedTranslations) {
            this.storeTranslations(language, fetchedTranslations);
          }
        }
      });
    };
    await start();
  }

  // Wait for translations to initialize, then configure i18next

  async init(availableLanguages: string[], language: string, defaultSystemLanguage: string, store: Store): Promise<i18n> {
    this.store = store;
    this.subscribeToStore(store);
    await this.initTranslations(availableLanguages);
    return this.initI18n(availableLanguages, language, defaultSystemLanguage);
  }

  initI18n(availableLanguages: string[], language: string, fallbackLanguage: string): i18n {
    /** configure translation package */
    void i18next.init({
      whitelist: availableLanguages,
      fallbackLng: fallbackLanguage,
      lng: language,
      keySeparator: '/',
      // Otherwise we cannot use keys with an included colon. This happens at the userlist where we have group filters.
      // There the label key is dynamic cannot be translated by key. Since we need a colon at the label nonetheless
      // we have to add it there at the end of the "translation key" which leads to an empty translation.
      nsSeparator: '::',
      initImmediate: false,
      preload: availableLanguages,
      debug: apiConfig.debug,
      resources: availableLanguages.reduce((acc: Resource, lang) => {
        const translations = this.getStoredTranslations(lang);
        if (translations) {
          acc[lang] = {translation: translations};
        }
        return acc;
      }, {}),
      returnEmptyString: true,
      returnNull: true,
      interpolation: {
        escapeValue: false, // not needed for react!!
        formatSeparator: ',',

        format: function (value, format?: string) {
          if (format === 'uppercase' && typeof value === 'string') return value.toUpperCase();
          // formatfunction is typed with `any` parameter. sad.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return value;
        },
      },
    });

    return i18next
  }

  getStoredTranslations(language: string): Record<string, string> | null {
    const translation = localStorage.getItem(this.getLocalStorageTranslationKey(language));
    if (typeof translation === 'string') {
      try {
        return JSON.parse(translation) as Record<string, string>;
      } catch {
        return null;
      }
    }
    return null;
  }

  getLocalStorageTranslationKey(language: string) {
    return this.STORAGE_PREFIX_TRANSLATION + language;
  }

  getLocalStorageLastUpdateKey(language: string) {
    return this.STORAGE_PREFIX_TRANSLATION_WRITE_TIME + language;
  }

  getStoredWriteTime(language: string): string|null {
    const timeString = localStorage.getItem(this.getLocalStorageLastUpdateKey(language));
    if (timeString) {
      return JSON.parse(timeString) as string;
    }
    return null;
  }

  storeTranslations(language: string, tmpTranslations: { [key: string]: string }) {
    localStorage.setItem(this.getLocalStorageTranslationKey(language), JSON.stringify(tmpTranslations));
    localStorage.setItem(this.getLocalStorageLastUpdateKey(language), JSON.stringify(moment().toJSON()));
  }

  fetchTranslations(language: string): Promise<Record<string, string>|void> {
    return llsAPI
      .get<Record<string, string>>(this.getTranslationUrlForLanguage(language))
      .then(resp => resp.data)
      .catch((err: Error) => {
        logger.info('Error fetching translations', {
          error: err,
          info: {
            componentStack: 'translation.js',
          },
        });
      });
  }

  getTranslationUrlForLanguage(language: string) {
    return `${apiConfig.translation_service.host}/api/translations/export/${language}/${
      apiConfig.translation_service.published ? 'published' : 'unpublished'
    }?tags=loop_x`;
  }

  async fetchIsTranslationCacheOutdated(language: string) {
    return llsAPI
      .get(this.getTranslationLastUpdateUrlForLanguage(language))
      .then(resp => {
        if (resp.data) {
          const localLastUpdate = this.getStoredWriteTime(language);
          const storedTranslation = this.getStoredTranslations(language);
          if (!storedTranslation || localLastUpdate === undefined || localLastUpdate === null) {
            return true;
          }
          const lastServerUpdate = moment(this.cutDateString(resp.data), '');
          if (!lastServerUpdate) { // this must not be
            logger.warn('could not get last translation update, response empty!');
            return true;
          }
          const lastLocalUpdate = moment(this.cutDateString(localLastUpdate));
          if (lastServerUpdate.isAfter(lastLocalUpdate)) {
            return true;
          }
        }
        return false;
      })
      .catch((err: Error) => {
        logger.info('Error fetchTranslationsLastUpdate', {
          error: err,
          info: {
            componentStack: 'translation.js',
          },
        });
      });
  }

  getTranslationLastUpdateUrlForLanguage(language: string) {
    return `${apiConfig.translation_service.host}/api/translations/last-update/${language}/${
      apiConfig.translation_service.published ? 'published' : 'unpublished'
    }`;
  }

  cutDateString(date: string) {
    const dateString = String(date);
    return `${dateString.substr(0, 19)}Z`;
  }

  subscribeToStore(store: Store) {
    store.subscribe(() => {
      const state = store.getState() as StoreI;
      let currentLocale: string = getSystemLocale(state);
      if (!localStorage.jwt) {
        currentLocale = getBrowserPreferredSystemLocale();
      }
      if (currentLocale && currentLocale !== this.lastLocale && isSystemLocaleAllowed(state, currentLocale)) {
        const currentLanguage = localeToLanguage(currentLocale)
        void i18next.changeLanguage(currentLanguage);
        moment.locale(currentLocale);
        moment.updateLocale(currentLanguage, {
          relativeTime: momentTranslations[currentLanguage],
        });
        this.lastLocale = currentLocale;
      }
    });
  }
}

const translations = new Translations();

export default translations;
